import React, {Component} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";
import {bindActionCreators} from "redux";
import {getSupplierOrder} from "./actions";
import {Alert, Button, Card, Input, InputNumber, Spin} from "antd";
import ResponsiveTable from "../common/ResponsiveTable";
import {receiveProduct} from "../Product/actions";
import {Link} from "react-router-dom";

class SupplierOrderPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    salonId: PropTypes.string,
    order: PropTypes.object,
    match: PropTypes.object.isRequired,
    /* actions */
    getSupplierOrder: PropTypes.func.isRequired,
    receiveProduct: PropTypes.func.isRequired,
  };

  static defaultProps = {
    salonId: null
  };

  state = {
    skus: {},
    quantities: {},
    netCosts: {},
    step: 'initiated',
    errors: [],
  };

  componentDidMount() {
    this._load().catch(console.error);
  }

  _load = () => {
    const {supplierOrderId} = this.props.match.params;
    return this.props.getSupplierOrder({supplierOrderId})
      .then(() => this.setState({step: 'loaded', skus: {}, quantities: {}, netCosts: {}}));
  };

  _handleReceiveClick = async(e) => {
    const {supplierOrderId} = this.props.match.params;
    const {receiveProduct, order} = this.props;
    e.preventDefault();
    this.setState({step: 'receiving'});
    const purchaseId = null, errors = [];
    for (let i = 0; i < order.items.length; i++) {
      const quantity = this.state.quantities[i];
      if (!quantity) continue;
      const orderItem = order.items[i];
      const productId = orderItem.productId;
      let sku, lotNumber;
      if (this.state.skus[i]) {
        sku = this.state.skus[i];
        lotNumber = '';
      } else {
        sku = orderItem.sku;
        lotNumber = orderItem.lotNumber;
      }
      const netCostPerUnit = this.state.netCosts[i] || 0;
      try {
        await receiveProduct({productId, purchaseId, quantity, netCostPerUnit, sku, lotNumber, supplierOrderId});
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || 'Unknown error';
        errors.push(`${order.items[i].productDisplay} failed to received due to ${msg}`);
      }
    }
    this.setState({errors});
    await new Promise(resolve => setTimeout(resolve, 500));
    await this._load();
  }

  render() {
    const {
      history, order
    } = this.props;
    const items = (order && order.items) || [];
    const dirty = Object.keys(this.state.quantities).length > 0;
    const isReceiving = this.state.step === 'receiving';
    const columns = [
      {
        title: 'Name',
        dataIndex: 'productDisplay',
        key: 'productDisplay'
      },
      {
        title: 'Quantity Ordered',
        dataIndex: 'quantity',
        key: 'quantityOrdered',
        width: 100,
      },
      {
        title: 'Quantity Received',
        dataIndex: 'received',
        key: 'quantityReceived',
        width: 100,
      },
      {
        title: 'Receive Quantity',
        key: 'receiveQuantity',
        width: 100,
        render: (_, order, index) => <InputNumber
          value={this.state.quantities[index] || 0}
          onChange={value => this.setState(state => ({...state, quantities: {...state.quantities, [index]: value }}))}
          onFocus={e => e.target.select()}
          disabled={isReceiving}
        />
      },
      {
        title: 'SKU',
        key: 'sku',
        width: 180,
        render: (_, order, index) => <Input
          type="text"
          value={this.state.skus[index] || order.sku}
          onChange={e => {
            const value = e.target.value;
            this.setState(state => ({...state, skus: {...state.skus, [index]: value}}));
          }}
          onFocus={e => e.target.select()}
          disabled={isReceiving}
        />
      },
      {
        title: 'Net Cost Per Unit',
        key: 'netCostPerUnit',
        width: 150,
        render: (_, order, index) => <InputNumber
          precision={3}
          value={this.state.netCosts[index] || 0}
          onChange={value => this.setState(state => ({...state, netCosts: {...state.netCosts, [index]: value }}))}
          onFocus={e => e.target.select()}
          disabled={isReceiving}
        />
      },
      {
        title: 'View',
        key: 'view',
        fixed: 'right',
        width: 150,
        render: (_, item) => {
          return (
            <React.Fragment>
              <Link to={`/product/history/${encodeURIComponent(item.productId)}#NewWindow`} target="_blank">History</Link>
              {' | '}
              <Link to={`/product/stocks/${encodeURIComponent(item.productId)}#NewWindow`} target="_blank">Stocks</Link>
            </React.Fragment>
          );
        },
      },
    ];
    return (
      <Card title={[
        <Button key="back" icon="arrow-left" onClick={() => history.goBack()} />,
        " Supplier Order"
      ]}>
        <Alert
          type="warning"
          message="Notes"
          description={
            <ul>
              <li>
                If a product is missing from this list, you&apos;ll need to receive it by searching the product list
                and then clicking stock and then clicking receive.
              </li>
              <li>
                If a product fail to receive please make sure it didn&apos;t work by viewing it&apos;s history or stocks
                before trying again.
              </li>
              <li>
                If a product was received with a bad quantity or net cost per unit, the values can be corrected in the
                stocks batches section of the product. The corrected values will not be updated here.
              </li>
            </ul>
          }
        />
        {this.state.errors.length ? (
          <Alert
            type="error"
            message="One or more error occurred"
            description={<ul>{this.state.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
            closable
            onClose={() => this.setState({errors:[]})}
          />
        ) : null}
        <ResponsiveTable
          columns={columns}
          dataSource={items}
          pagination={false}
          loading={this.state.step === 'initiated'}
        />
        <Button
          type="primary"
          disabled={!dirty || this.state.step !== 'loaded'}
          onClick={this._handleReceiveClick}
          style={{margin:'0.5rem 0'}}
        >
          <Spin spinning={isReceiving} />
          Receive
        </Button>
      </Card>
    );
  }
}

const mapStateToProps = state => ({
  salonId: state.salon.selectedSalonId,
  order: state.supplier.order,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  getSupplierOrder,
  receiveProduct,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(SupplierOrderPage);



// WEBPACK FOOTER //
// ./src/Supplier/SupplierOrderPage.js